/**
 * Droppable / draggable id vocabulary owned by the label sidebar.
 *
 * The page-level `DragDropContext` routes a drop by looking at these ids, so they must stay
 * stable and unambiguous: `drop-labelOrder-*` and `drop-labelRemove-*` never collide with
 * `drop-label-&lt;sectionId>` because the character following `label` differs.
 */

const SECTION_ORDER_PREFIX = 'drop-labelOrder-';
const SECTION_DROP_PREFIX = 'drop-label-';
const REMOVE_ZONE_PREFIX = 'drop-labelRemove-';
const SECTION_DRAG_PREFIX = 'drag-label-';

/** Dnd type of the outer droppable that reorders sections. */
export const SECTION_ORDER_DND_TYPE = 'labelOrder';

/**
 * Remount key for the section-order `Droppable` when nested item `Droppable`s change.
 *
 * `@hello-pangea/dnd` does not register nested droppables that mount after their parent
 * (a newly created label stays undroppable until refresh). Sorting keeps reorder-only
 * updates from remounting the tree.
 * @param sectionIds Visible section ids in the sidebar.
 * @returns Stable key that changes only when a section is added or removed.
 */
export function sectionDroppableRegistryKey(sectionIds: readonly string[]): string {
  return [...sectionIds].sort().join('|');
}

/**
 * Droppable id of the outer list that reorders sections.
 * @param droppableType Sidebar dnd type (`bot`, `prompt`, …).
 * @returns Id for the section-order droppable of that sidebar.
 */
export function sectionOrderDroppableId(droppableType: string): string {
  return `${SECTION_ORDER_PREFIX}${droppableType}`;
}

/**
 * Droppable id of a single section.
 * @param sectionId Id of the section that accepts the drop.
 * @returns Id the section's droppable registers under.
 */
export function sectionDroppableId(sectionId: string): string {
  return `${SECTION_DROP_PREFIX}${sectionId}`;
}

/**
 * Droppable id of the zone that drops an item out of its section.
 * @param droppableType Sidebar dnd type (`bot`, `prompt`, …).
 * @returns Id the remove zone registers under.
 */
export function removeZoneDroppableId(droppableType: string): string {
  return `${REMOVE_ZONE_PREFIX}${droppableType}`;
}

/**
 * Draggable id of a section header.
 * @param sectionId Id of the section being dragged.
 * @returns Id the section header registers under.
 */
export function sectionDraggableId(sectionId: string): string {
  return `${SECTION_DRAG_PREFIX}${sectionId}`;
}

/**
 * Draggable id of an item row.
 *
 * Includes the section id so the same underlying item can appear in Favorites and a main folder
 * without colliding — `@hello-pangea/dnd` requires unique `draggableId`s in one context.
 * Matches `DraggableItem` when its `id` is `${sectionId}::${itemId}`.
 * @param droppableType Sidebar dnd type (`bot`, `prompt`, …).
 * @param sectionId Id of the section that owns this row instance.
 * @param itemId Id of the bot / prompt / metric row.
 * @returns Id the item row registers under.
 */
export function itemDraggableId(droppableType: string, sectionId: string, itemId: string): string {
  return `drag-${droppableType}-${sectionId}::${itemId}`;
}

/**
 * Reads the section id out of a section droppable id.
 * @param droppableId Destination droppable id from a drop result.
 * @returns Section id, or `null` when the id is not a section droppable.
 */
export function parseSectionDroppableId(droppableId: string): string | null {
  if (!droppableId.startsWith(SECTION_DROP_PREFIX)) {
    return null;
  }
  return droppableId.slice(SECTION_DROP_PREFIX.length) || null;
}

/**
 * Reads the section id out of a section draggable id.
 * @param draggableId Dragged element id from a drop result.
 * @returns Section id, or `null` when the id is not a section draggable.
 */
export function parseSectionDraggableId(draggableId: string): string | null {
  if (!draggableId.startsWith(SECTION_DRAG_PREFIX)) {
    return null;
  }
  return draggableId.slice(SECTION_DRAG_PREFIX.length) || null;
}

/**
 * Reads the item id out of an item draggable id for a given sidebar type.
 *
 * Accepts section-scoped ids (`drag-&lt;type>-&lt;sectionId>::&lt;itemId>`) and legacy
 * unscoped ids (`drag-&lt;type>-&lt;itemId>`) used by editors that drag into the sidebar.
 * @param droppableType Sidebar dnd type (`bot`, `prompt`, …).
 * @param draggableId Dragged element id from a drop result.
 * @returns Item id, or `null` when the draggable belongs to another sidebar.
 */
export function parseItemDraggableId(droppableType: string, draggableId: string): string | null {
  const prefix = `drag-${droppableType}-`;
  if (!draggableId.startsWith(prefix)) {
    return null;
  }
  const rest = draggableId.slice(prefix.length);
  if (!rest) {
    return null;
  }
  const separatorIndex = rest.indexOf('::');
  if (separatorIndex === -1) {
    return rest;
  }
  return rest.slice(separatorIndex + 2) || null;
}
